import type { RouteRecordRaw } from 'vue-router'

import { getHomeRoute } from '@/app/router/home'
import {
  ROUTE_FORGOT_PASSWORD,
  ROUTE_HOME,
  ROUTE_LOGIN,
  ROUTE_MAINTENANCE,
  ROUTE_NOT_AUTHORIZED,
  ROUTE_NOT_FOUND,
  ROUTE_PROFILE,
  ROUTE_REGISTER,
  ROUTE_SET_PASSWORD,
} from '@/app/router/names'
import { useAuthStore } from '@/app/stores/auth'
import { MERGED_INTO_HR } from '@/app/stores/session'

/**
 * The shell's routes (wp/shell §3; paths unchanged by the manifest). The auth, error and maintenance pages are public
 * and render in the full layout; `/profile` needs a session (OPEN-2 (a)). `/debug` and `/pages/miscellaneous/error`
 * are dropped (NEW-19); the catch-all lives in src/app/router.
 */
const routes: RouteRecordRaw[] = [
  {
    path: '/',
    name: ROUTE_HOME,
    // Computed at navigation time (legacy computed it once, at load — wp/shell §4.1).
    redirect: () => getHomeRoute(useAuthStore().session),
  },
  {
    path: '/login',
    name: ROUTE_LOGIN,
    component: () => import('./views/LoginView.vue'),
    meta: { layout: 'full', public: true, redirectIfLoggedIn: true },
  },
  {
    path: '/register',
    name: ROUTE_REGISTER,
    component: () => import('./views/RegisterView.vue'),
    meta: { layout: 'full', public: true, redirectIfLoggedIn: true },
  },
  {
    path: '/forgot-password',
    name: ROUTE_FORGOT_PASSWORD,
    component: () => import('./views/ForgotPasswordView.vue'),
    meta: { layout: 'full', public: true, redirectIfLoggedIn: true },
  },
  {
    // No redirectIfLoggedIn: an invitation opened while signed in ends the local session (wp/shell §4.6).
    path: '/set-password',
    name: ROUTE_SET_PASSWORD,
    component: () => import('./views/SetPasswordView.vue'),
    meta: { layout: 'full', public: true },
  },
  {
    path: '/not-authorized',
    name: ROUTE_NOT_AUTHORIZED,
    component: () => import('./views/NotAuthorizedView.vue'),
    meta: { layout: 'full', public: true },
  },
  {
    path: '/under-maintenance',
    name: ROUTE_MAINTENANCE,
    component: () => import('./views/UnderMaintenanceView.vue'),
    meta: { layout: 'full', public: true },
  },
  {
    path: '/error-404',
    name: ROUTE_NOT_FOUND,
    component: () => import('./views/Error404View.vue'),
    meta: { layout: 'full', public: true },
  },
  {
    // Vertical layout, no breadcrumb (legacy `pages.js:103-108`).
    path: '/profile',
    name: ROUTE_PROFILE,
    component: () => import('./views/ProfileView.vue'),
    meta: { resource: 'always' },
  },
  // BR-097 (owner, 2026-11-03): Onboarding and Engagement are part of Human resources. Every `/on/…` and `/en/…`
  // address — bookmarks, the API's e-mail links, old sessions — opens the same page under `/hr/…`, query and hash
  // kept. A legacy redirect of the manifest is more specific and still wins where one exists (`/on/onboarding/:id`).
  ...MERGED_INTO_HR.map(
    (code): RouteRecordRaw => ({
      path: `/${code}/:rest(.*)*`,
      redirect: (to) => ({
        path: `/hr/${Array.isArray(to.params.rest) ? to.params.rest.join('/') : (to.params.rest ?? '')}`,
        query: to.query,
        hash: to.hash,
      }),
    }),
  ),
]

export default routes
